import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaUser, FaEnvelope, FaLock, FaPhone, FaSpinner, FaBuilding, FaCheckSquare, FaSquare, FaExternalLinkAlt, FaUserShield, FaClock, FaCheckCircle, FaWhatsapp, FaPhoneAlt, FaCheck, FaCrown, FaHandshake, FaGift } from 'react-icons/fa';
import FloatingWhatsApp from '../components/FloatingWhatsApp';

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    role: 'client',
    plan: 'plus'
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedDataProtection, setAcceptedDataProtection] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [showPaidRedirect, setShowPaidRedirect] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!acceptedTerms) {
      setError('You must accept the Terms & Conditions to register');
      return;
    }

    if (formData.role === 'advisor' && !acceptedDataProtection) {
      setError('As an Advisor, you must accept the Data Protection & Confidentiality Undertaking');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    const userData = {
      name: formData.name,
      email: formData.email,
      password: formData.password,
      phone: formData.phone,
      role: formData.role,
      plan: formData.plan,
      is_active: true
    };

    const result = await register(userData);
    
    if (result.success) {
      if (formData.plan === 'pro_plus') {
        setShowPaidRedirect(true);
      } else {
        setRegistrationSuccess(true);
      }
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  // Paid plan redirect page
  if (showPaidRedirect) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" 
           style={{ background: 'linear-gradient(135deg, #0A1929 0%, #1E3A5F 100%)' }}>
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-5xl font-bold mb-2" style={{ color: '#FFD700' }}>Arth Gurukul</h1>
            <p className="text-[#FFD700] text-lg font-semibold">Your School for Lifelong Financial Wellness</p>
          </div>
          <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl shadow-2xl p-8 border border-[#FFD700]/50" data-testid="paid-redirect-card">
            <div className="text-center">
              <div className="mb-6 flex justify-center">
                <div className="bg-[#FFD700]/20 rounded-full p-4">
                  <FaCrown className="text-[#FFD700] text-4xl" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Account Created!</h2>
              <div className="bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-lg p-4 mb-6">
                <FaCheckCircle className="text-green-400 text-xl mx-auto mb-2" />
                <p className="text-gray-300 text-sm mb-2">
                  Your paid account is pending admin approval.
                </p>
                <p className="text-[#FFD700] font-medium">
                  Talk to your consultant to activate your membership.
                </p>
              </div>
              <p className="text-[10px] text-gray-500 italic mb-4 border-l-2 border-[#FFD700]/30 pl-2 leading-relaxed text-left" data-testid="register-paid-disclaimer">
                The membership fee is for financial wellness education &amp; financial wellness tools only. No fees are charged by the platform for any consultation or advice by a financial consultant or planner.
              </p>
              <div
                className="inline-flex flex-col items-center justify-center w-full py-3 px-4 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg text-center gap-1 mb-3"
                data-testid="paid-talk-to-consultant"
              >
                <span className="flex items-center gap-2">
                  <FaHandshake />
                  Talk to your consultant
                </span>
                <span className="text-[10px] font-normal opacity-80">Contact details available on the website</span>
              </div>
              <Link 
                to="/login"
                className="inline-block w-full py-3 px-4 border border-gray-600 text-gray-300 font-medium rounded-lg hover:bg-gray-800 transition-all text-center text-sm"
                data-testid="go-to-login-btn"
              >
                Go to Login
              </Link>
            </div>
          </div>
          <div className="mt-8 text-center text-gray-400 text-xs">
            <p>&copy; 2026 Arth Gurukul. All rights reserved.</p>
          </div>
        </div>
      </div>
    );
  }

  // Free plan success page
  if (registrationSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" 
           style={{ background: 'linear-gradient(135deg, #0A1929 0%, #1E3A5F 100%)' }}>
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-5xl font-bold mb-2" style={{ color: '#FFD700' }}>Arth Gurukul</h1>
            <p className="text-[#FFD700] text-lg font-semibold">Your School for Lifelong Financial Wellness</p>
          </div>
          <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl shadow-2xl p-8 border border-cyan-500/50" data-testid="registration-pending-card">
            <div className="text-center">
              <div className="mb-6 flex justify-center">
                <div className="bg-cyan-500/20 rounded-full p-4">
                  <FaClock className="text-cyan-400 text-4xl" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Registration Submitted!</h2>
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4 mb-6">
                <FaCheckCircle className="text-green-400 text-xl mx-auto mb-2" />
                <p className="text-gray-300 text-sm mb-2">
                  Your <span className="text-[#FFD700] font-semibold">Plus (Free)</span> account has been created.
                </p>
                <p className="text-cyan-400 font-medium">
                  It is now pending approval from the administrator.
                </p>
              </div>
              <p className="text-gray-400 text-sm mb-6">
                Once approved, you'll have access to the <strong className="text-white">My Family Should Know</strong> vault. 
                You can upgrade to PRO+ anytime to unlock all features.
              </p>
              <Link 
                to="/login"
                className="inline-block w-full py-3 px-4 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] focus:outline-none transition-all transform hover:scale-[1.02] text-center"
                data-testid="go-to-login-btn"
              >
                Go to Login
              </Link>
            </div>
          </div>
          <div className="mt-8 text-center text-gray-400 text-xs">
            <p>&copy; 2026 Arth Gurukul. All rights reserved.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" 
         style={{ background: 'linear-gradient(135deg, #0A1929 0%, #1E3A5F 100%)' }}>
      <div className="w-full max-w-2xl">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold mb-2" style={{ color: '#FFD700' }}>
            Arth Gurukul
          </h1>
          <p className="text-[#FFD700] text-lg font-semibold">Your School for Lifelong Financial Wellness</p>
          <p className="text-gray-400 text-sm mt-1">Navigate. Plan. Prosper.</p>
        </div>

        {/* Plan Selection Cards - Only for Client role */}
        {formData.role === 'client' && (
          <div className="grid grid-cols-2 gap-4 mb-6" data-testid="plan-selection">
            {/* Plus (Free) Card */}
            <button
              type="button"
              onClick={() => setFormData({ ...formData, plan: 'plus' })}
              className={`relative p-5 rounded-xl border-2 text-left transition-all ${
                formData.plan === 'plus'
                  ? 'border-cyan-400 bg-cyan-500/10 shadow-lg shadow-cyan-500/10'
                  : 'border-[#2D5A8F] bg-[#1A2942]/50 hover:border-gray-500'
              }`}
              data-testid="plan-plus-btn"
            >
              {formData.plan === 'plus' && (
                <div className="absolute top-3 right-3">
                  <FaCheckCircle className="text-cyan-400" />
                </div>
              )}
              <div className="flex items-center gap-2 mb-2">
                <FaGift className="text-cyan-400" />
                <span className="text-white font-bold">Plus</span>
              </div>
              <div className="text-2xl font-bold text-cyan-400 mb-2">FREE</div>
              <div className="space-y-1.5 text-xs">
                <div className="flex items-center gap-1.5 text-gray-300">
                  <FaCheck className="text-cyan-400 flex-shrink-0" size={10} />
                  <span>My Family Should Know</span>
                </div>
                <div className="flex items-center gap-1.5 text-gray-300">
                  <FaCheck className="text-cyan-400 flex-shrink-0" size={10} />
                  <span>Profile & Settings</span>
                </div>
                <div className="flex items-center gap-1.5 text-gray-300">
                  <FaCheck className="text-cyan-400 flex-shrink-0" size={10} />
                  <span>DigiLocker Access</span>
                </div>
              </div>
              <p className="text-gray-500 text-[10px] mt-3">Start for free, upgrade anytime</p>
            </button>

            {/* PRO+ (Paid) Card */}
            <button
              type="button"
              onClick={() => setFormData({ ...formData, plan: 'pro_plus' })}
              className={`relative p-5 rounded-xl border-2 text-left transition-all ${
                formData.plan === 'pro_plus'
                  ? 'border-[#FFD700] bg-[#FFD700]/10 shadow-lg shadow-[#FFD700]/10'
                  : 'border-[#2D5A8F] bg-[#1A2942]/50 hover:border-gray-500'
              }`}
              data-testid="plan-pro-btn"
            >
              <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] text-[10px] font-bold rounded-full">
                RECOMMENDED
              </div>
              {formData.plan === 'pro_plus' && (
                <div className="absolute top-3 right-3">
                  <FaCheckCircle className="text-[#FFD700]" />
                </div>
              )}
              <div className="flex items-center gap-2 mb-2">
                <FaCrown className="text-[#FFD700]" />
                <span className="text-white font-bold">PRO+</span>
              </div>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-gray-500 line-through text-sm">₹2,999</span>
                <span className="text-2xl font-bold text-[#FFD700]">₹999</span>
                <span className="text-gray-400 text-xs">/yr</span>
              </div>
              <div className="space-y-1.5 text-xs">
                <div className="flex items-center gap-1.5 text-gray-300">
                  <FaCheck className="text-[#FFD700] flex-shrink-0" size={10} />
                  <span>All Plus features</span>
                </div>
                <div className="flex items-center gap-1.5 text-gray-300">
                  <FaCheck className="text-[#FFD700] flex-shrink-0" size={10} />
                  <span>360° Financial Dashboard</span>
                </div>
                <div className="flex items-center gap-1.5 text-gray-300">
                  <FaCheck className="text-[#FFD700] flex-shrink-0" size={10} />
                  <span>All Premium Features</span>
                </div>
              </div>
              <p className="text-[#FFD700]/70 text-[10px] mt-3">Full access to everything</p>
            </button>
          </div>
        )}

        {/* Register Card */}
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl shadow-2xl p-8 border border-[#2D5A8F]">
          <h2 className="text-2xl font-bold text-white mb-6 text-center">
            {formData.role === 'client' && formData.plan === 'plus' ? 'Register — Plus (Free)' : 
             formData.role === 'client' && formData.plan === 'pro_plus' ? 'Register — PRO+' : 'Register'}
          </h2>
          
          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500 rounded-lg text-red-200 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Full Name */}
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">Full Name *</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaUser className="text-gray-400" />
                  </div>
                  <input type="text" name="name" value={formData.name} onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20 transition-all"
                    placeholder="John Doe" required />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">Email Address *</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaEnvelope className="text-gray-400" />
                  </div>
                  <input type="email" name="email" value={formData.email} onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20 transition-all"
                    placeholder="you@example.com" required />
                </div>
              </div>

              {/* Phone */}
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">Phone Number</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaPhone className="text-gray-400" />
                  </div>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20 transition-all"
                    placeholder="+91 98765 43210" />
                </div>
              </div>

              {/* Role */}
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">Role *</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaBuilding className="text-gray-400" />
                  </div>
                  <select name="role" value={formData.role} onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white focus:outline-none focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20 transition-all"
                    required>
                    <option value="client">Client</option>
                    <option value="advisor">Financial Advisor</option>
                    <option value="manager">Manager</option>
                  </select>
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">Password *</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaLock className="text-gray-400" />
                  </div>
                  <input type="password" name="password" value={formData.password} onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20 transition-all"
                    placeholder="Min 6 characters" required />
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">Confirm Password *</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaLock className="text-gray-400" />
                  </div>
                  <input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20 transition-all"
                    placeholder="Re-enter password" required />
                </div>
              </div>
            </div>

            {/* Terms & Conditions */}
            <div className="mt-6 p-4 bg-[#0A1929] rounded-lg border border-[#2D5A8F]">
              <div className="flex items-start space-x-3">
                <button type="button" onClick={() => setAcceptedTerms(!acceptedTerms)}
                  className="flex-shrink-0 mt-0.5 focus:outline-none" data-testid="terms-checkbox">
                  {acceptedTerms ? <FaCheckSquare className="text-[#FFD700] text-xl" /> : <FaSquare className="text-gray-500 text-xl hover:text-gray-400 transition-colors" />}
                </button>
                <div className="text-sm">
                  <label className="text-gray-300 cursor-pointer" onClick={() => setAcceptedTerms(!acceptedTerms)}>
                    I have read and agree to the{' '}
                    <Link to="/terms-and-conditions" target="_blank" className="text-[#FFD700] hover:text-[#D4AF37] font-medium inline-flex items-center" onClick={(e) => e.stopPropagation()}>
                      Terms & Conditions <FaExternalLinkAlt className="ml-1" size={10} />
                    </Link>
                    {' '}and{' '}
                    <Link to="/privacy-policy" target="_blank" className="text-[#FFD700] hover:text-[#D4AF37] font-medium inline-flex items-center" onClick={(e) => e.stopPropagation()}>
                      Privacy Policy <FaExternalLinkAlt className="ml-1" size={10} />
                    </Link>
                    {' '}of Arth Gurukul.
                  </label>
                </div>
              </div>
            </div>

            {/* Advisor Data Protection */}
            {formData.role === 'advisor' && (
              <div className="mt-4 p-4 bg-[#0A1929] rounded-lg border border-blue-500/50">
                <div className="flex items-start space-x-3">
                  <button type="button" onClick={() => setAcceptedDataProtection(!acceptedDataProtection)}
                    className="flex-shrink-0 mt-0.5 focus:outline-none" data-testid="data-protection-checkbox">
                    {acceptedDataProtection ? <FaCheckSquare className="text-blue-400 text-xl" /> : <FaSquare className="text-gray-500 text-xl hover:text-gray-400 transition-colors" />}
                  </button>
                  <div className="text-sm">
                    <div className="flex items-center mb-1">
                      <FaUserShield className="text-blue-400 mr-2" />
                      <span className="text-blue-400 font-medium text-xs uppercase tracking-wide">Advisor Agreement</span>
                    </div>
                    <label className="text-gray-300 cursor-pointer" onClick={() => setAcceptedDataProtection(!acceptedDataProtection)}>
                      I have read and agree to the{' '}
                      <Link to="/advisor-data-protection" target="_blank" className="text-blue-400 hover:text-blue-300 font-medium inline-flex items-center" onClick={(e) => e.stopPropagation()}>
                        Advisor Data Protection & Confidentiality Undertaking <FaExternalLinkAlt className="ml-1" size={10} />
                      </Link>
                      {' '}and agree to comply with all obligations stated herein.
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button type="submit"
              disabled={loading || !acceptedTerms || (formData.role === 'advisor' && !acceptedDataProtection)}
              className="w-full py-3 px-4 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] focus:outline-none focus:ring-2 focus:ring-[#FFD700] focus:ring-offset-2 focus:ring-offset-[#1E3A5F] transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="register-submit-btn">
              {loading ? (
                <><FaSpinner className="animate-spin" /> Creating Account...</>
              ) : formData.role === 'client' && formData.plan === 'pro_plus' ? (
                <><FaCrown /> Create Account & Pay ₹999/year</>
              ) : (
                'Create Free Account'
              )}
            </button>
          </form>

          {/* Login Link */}
          <div className="mt-6 text-center">
            <p className="text-gray-400 text-sm">
              Already have an account?{' '}
              <Link to="/login" className="text-[#FFD700] hover:text-[#D4AF37] font-medium transition-colors">
                Sign in here
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center space-y-3">
          <a href="https://wa.link/c15uzz" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-lg hover:from-green-600 hover:to-green-700 transition-all transform hover:scale-[1.02]"
            data-testid="book-demo-btn">
            <FaWhatsapp size={18} /><span>Book Your Free Demo</span>
          </a>
          
          <div className="flex items-center justify-center text-sm">
            <a href="mailto:care@mdrtschool.com" className="flex items-center space-x-2 text-[#FFD700] hover:text-[#D4AF37] transition-colors">
              <FaEnvelope size={14} />
              <span>Tech &amp; Training Support only: care@mdrtschool.com</span>
            </a>
          </div>
          
          <div className="text-gray-400 text-xs">
            <p>&copy; 2026 Arth Gurukul | Tech, Tools &amp; Training Powered by MDRT School | By Financial Nirvana. All rights reserved.</p>
            <p className="mt-1 text-[#FFD700]">Your School for Lifelong Financial Wellness</p>
          </div>
          
          <div className="mt-4 text-gray-500 text-[10px] leading-relaxed max-w-md mx-auto">
            <p><strong className="text-gray-400">Disclaimer:</strong> MDRT School and Financial Nirvana do not provide any financial, investment, legal, or tax advisory services. We are a capability development and education platform offering tools, technology, and training exclusively for financial services sales professionals. Any decisions taken based on the use of our content, tools, or programmes are solely the responsibility of the user.</p>
          </div>
        </div>
      </div>
      
      <FloatingWhatsApp phoneLink="https://wa.link/c15uzz" message="Need help? Chat with us!" />
    </div>
  );
};

export default Register;
